import { useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

// Process raw permissions into faster lookup structure
const normalizePermissions = (rawPermissions = []) => {
  const map = {};

  for (const perm of rawPermissions) {
    const module = perm.module;

    map[module] = {
      topLevel: new Set(perm.topLevel || []), // e.g. ['view', 'edit']
      viewableFields: new Set(perm.viewableFields || []),
      editableFields: new Set(perm.editableFields || []),
      hasWildcardTopLevel: (perm.topLevel || []).includes('all'),
      hasWildcardView: (perm.viewableFields || []).includes('all'),
      hasWildcardEdit: (perm.editableFields || []).includes('all'),
    };
  }

  return map;
};

// Deep-compare raw permissions arrays by value
const permissionsEqual = (a, b) => {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const pa = a[i], pb = b[i];
    if (pa.module !== pb.module) return false;
    const tla = pa.topLevel || [], tlb = pb.topLevel || [];
    if (tla.length !== tlb.length || tla.some((v, j) => v !== tlb[j])) return false;
    const vfa = pa.viewableFields || [], vfb = pb.viewableFields || [];
    if (vfa.length !== vfb.length || vfa.some((v, j) => v !== vfb[j])) return false;
    const efa = pa.editableFields || [], efb = pb.editableFields || [];
    if (efa.length !== efb.length || efa.some((v, j) => v !== efb[j])) return false;
  }
  return true;
};


export const usePermissions = () => {
  const { user } = useAuth();
  const role = user?.role || '';
  // Fix: get permissions from user.permissionSet.permissions if present, else user.permissions
  const rawPermissions =
    user?.permissionSet?.permissions && Array.isArray(user.permissionSet.permissions)
      ? user.permissionSet.permissions
      : user?.permissions || [];

  // Stabilize the reference using a ref with deep comparison
  const prevRef = useRef(rawPermissions);
  if (!permissionsEqual(prevRef.current, rawPermissions)) {
    prevRef.current = rawPermissions;
  }
  const stablePermissions = prevRef.current;

  const permissionMap = useMemo(() => normalizePermissions(stablePermissions), [stablePermissions]);

  // Global admin override. Admins always bypass — matching server-side enforcement
  // (middlewares/authorize.js). The previous "every module must have 'all'" check
  // was brittle: adding any new module silently stripped admins of wildcard access.
  const isWildcardAdmin = useMemo(() => role && role.toLowerCase() === 'admin', [role]);

  // Check top-level permission on module, e.g. 'view' or 'edit'
  const canViewModule = (module) => {
    if (isWildcardAdmin) return true;
    const mod = permissionMap[module];
    if (!mod) return false;
    return mod.hasWildcardTopLevel || mod.topLevel.has('view');
  };

  const canViewCreateModule = (module) => {
    if (isWildcardAdmin) return true;
    const mod = permissionMap[module];
    if (!mod) return false;
    return mod.hasWildcardTopLevel || mod.topLevel.has('create');
  };

  const canEditModule = (module) => {
    if (isWildcardAdmin) return true;
    const mod = permissionMap[module];
    if (!mod) return false;
    return mod.hasWildcardTopLevel || mod.topLevel.has('edit');
  };

  // Check field-level permission only if module top-level permits it
  const canViewField = (module, field) => {
    if (isWildcardAdmin) return true;
    const mod = permissionMap[module];
    if (!mod) return false;
    // Must have module view AND field view permission
    const moduleViewAllowed = mod.hasWildcardTopLevel || mod.topLevel.has('view');
    if (!moduleViewAllowed) return false;
    return mod.hasWildcardView || mod.viewableFields.has(field);
  };

  const canEditField = (module, field) => {
    if (isWildcardAdmin) return true;
    const mod = permissionMap[module];
    if (!mod) return false;
    // Must have module edit OR create AND field edit permission
    const moduleEditAllowed = mod.hasWildcardTopLevel || mod.topLevel.has('edit') || mod.topLevel.has('create');
    if (!moduleEditAllowed) return false;
    return mod.hasWildcardEdit || mod.editableFields.has(field);
  };

  // Other helpers (optional)
  const can = (module, action) => {
    if (isWildcardAdmin) return true;
    const mod = permissionMap[module];
    if (!mod) return false;
    return mod.hasWildcardTopLevel || mod.topLevel.has(action);
  };

  const hasModule = (module) => !!permissionMap[module];
  const getModule = (module) => permissionMap[module];

  return {
    role,
    isWildcardAdmin,
    hasModule,
    can,
    canViewModule,
    canEditModule,
    canViewField,
    canEditField,
    getModule,
    canViewCreateModule
  };
};
